import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight, Star, CheckCircle2, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { PricingCalculator } from "@/components/PricingCalculator";
import { getLocalContent } from "@/lib/city-content";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { REGIONS } from "@/lib/locations";

const BOOKING_URL = "/book";

// Map region slugs to Chinese city pages
const REGION_TO_ZH: Record<string, string> = {
  "sacramento-area": "/zh/sacramento",
  "contra-costa":    "/zh/contra-costa",
  "solano":          "/zh/solano",
  "alameda":         "/zh/alameda",
  "san-diego":       "/zh/san-diego",
  "orange-county":   "/zh/orange-county",
  "santa-clara":     "/zh/santa-clara",
  "marin":           "/zh/marin",
  "san-francisco":   "/zh/san-francisco",
  "los-angeles":     "/zh/los-angeles",
  "phoenix-area":    "/zh/phoenix",
};

// Localized region names in Chinese
const REGION_NAMES_ZH: Record<string, string> = {
  "sacramento-area": "沙加缅度地区",
  "contra-costa":    "康特拉科斯塔县",
  "solano":          "索拉诺县",
  "alameda":         "阿拉米达县",
  "san-diego":       "圣地亚哥地区",
  "orange-county":   "橙县",
  "santa-clara":     "圣克拉拉县",
  "marin":           "马林县",
  "san-francisco":   "旧金山地区",
  "los-angeles":     "洛杉矶县",
  "phoenix-area":    "凤凰城地区",
};

interface ChineseHeroProps {
  cityName: string;
  regionSlug: string;
}

export const ChineseHero = ({ cityName, regionSlug }: ChineseHeroProps) => {
  const localContent = getLocalContent(cityName, regionSlug, 'zh');
  const currentRegionName = REGION_NAMES_ZH[regionSlug] || cityName;
  const [cleanersCount, setCleanersCount] = useState(3);

  useEffect(() => {
    const day = new Date().getDate();
    setCleanersCount((day % 5) + 2);
  }, []);

  return (
    <div className="relative min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden bg-background">
      <div className="absolute inset-0 z-0">
        <img
          srcSet="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=800&q=80 800w, https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=1920&q=80 1920w"
          sizes="(max-width: 768px) 800px, 1920px"
          src="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=1920&q=80"
          alt={`${cityName}专业家庭清洁服务`}
          className="h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="1920"
          height="700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
      </div>

      <div className="container relative z-10 pt-36 pb-12 lg:pt-48 lg:pb-24">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-3 mb-6 anim-fade-up is-visible delay-100">
            {/* Region Selector Dropdown */}
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary-hover transition-colors outline-none font-bold text-sm shadow-md">
                    <MapPin className="h-4 w-4" />
                    {currentRegionName}
                    <ChevronDown className="h-4 w-4 opacity-70" />
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top" className="font-medium">
                  <p>点击更改您的服务区域</p>
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="start" className="w-60 p-2 max-h-80 overflow-y-auto">
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">加利福尼亚州</DropdownMenuLabel>
                {Object.entries(REGIONS).filter(([slug]) => slug !== 'phoenix-area').map(([slug]) => (
                  <DropdownMenuItem key={slug} asChild>
                    <Link
                      to={REGION_TO_ZH[slug] || `/zh/${slug}`}
                      className="font-medium cursor-pointer"
                    >
                      {REGION_NAMES_ZH[slug] || slug}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <div className="h-px bg-border my-1" />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">亚利桑那州</DropdownMenuLabel>
                {Object.entries(REGIONS).filter(([slug]) => slug === 'phoenix-area').map(([slug]) => (
                  <DropdownMenuItem key={slug} asChild>
                    <Link
                      to={`/zh/${slug}`}
                      className="font-medium cursor-pointer"
                    >
                      凤凰城地区
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Star rating row removed */}

            <div className="flex items-center gap-1.5 bg-accent/10 px-3 py-1.5 rounded-full border border-accent/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
              </span>
              <span className="text-sm font-bold text-accent">{cityName} 的热门选择</span>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6 text-foreground leading-[1.1] anim-fade-up is-visible delay-200">
            {cityName}家庭清洁服务。<br className="hidden md:block" />
            即刻获取精准报价。
          </h1>

          <p className="text-xl md:text-2xl text-foreground mb-2 max-w-lg font-semibold anim-fade-up is-visible delay-300">
            60秒内完成预约
          </p>
          <p className="text-base md:text-lg text-muted-foreground mb-2 max-w-lg font-medium anim-fade-up is-visible delay-400">
            无隐藏费用。透明定价。200%满意保证。
          </p>
          <p className="text-sm md:text-base text-muted-foreground mb-8 max-w-lg anim-fade-up is-visible delay-500">
            {localContent.heroSubtitle}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4 anim-fade-up is-visible delay-500">
            <Button size="lg" className="h-14 px-8 text-lg font-bold rounded-full shadow-elegant hover:shadow-lg transition-all hover:-translate-y-1" asChild>
              <Link to={BOOKING_URL}>
                获取即时报价 <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-8 text-lg font-bold rounded-full bg-background/50 backdrop-blur-md border-border/50 hover:bg-background/80" asChild>
              <Link to="/services">
                查看服务
              </Link>
            </Button>
          </div>

          {/* Real-time cleaner availability widget */}
          <div className="mt-8 bg-background/90 backdrop-blur-xl border border-border/50 rounded-2xl p-4 shadow-elegant max-w-md anim-fade-up is-visible delay-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
                <span className="font-bold text-foreground text-xs uppercase tracking-wider">实时可用性</span>
              </div>
              <span className="text-xs font-semibold text-muted-foreground bg-muted/80 px-2.5 py-1 rounded-md">{cityName}</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                {cleanersCount}
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">
                  您所在地区有 {cleanersCount} 名保洁员
                </p>
                <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  下一个空档: <span className="text-primary font-bold">{new Date().getHours() < 14 ? "今天" : "明天"}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm font-medium text-muted-foreground anim-fade-up is-visible delay-600">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 无需承诺</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 随时取消</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 持牌且投保</span>
          </div>

          <div className="mt-8 anim-fade-up is-visible delay-700">
            <Link to="/" className="text-sm text-muted-foreground underline hover:text-primary transition-colors">
              🇺🇸 View in English
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
